import type { UserRole } from '@hap/constants'

export enum Collection {
  ChannelLogs = 'contactChannelLogs',
  Channels = 'contactChannels',
  Departments = 'spaces',
  Entities = 'entities',
  FileUploads = 'fileUploads',
  ModelTemplates = 'modelTemplates',
  HelpTopics = 'helpTopics',
  Journeys = 'journeys',
  Organizations = 'organizations',
  ProcedureGraphs = 'procedureGraphs',
  Session = 'session',
  TagGroups = 'tagGroups',
  Tags = 'tags',
  Teams = 'teams',
  TicketInteractions = 'ticketInteractions',
  Tickets = 'tickets',
  TranscriptionKeywords = 'transcriptionKeywords',
  Users = 'users',
}

export type RelayMethods = 'get' | 'post' | 'put' | 'delete'
export interface RelayRoute {
  method: RelayMethods
  url: string
  headers?: { [key: string]: string }
}

export interface PasswordPrompt {
  currentPassword?: string
}

export type RelayEndpointConfig = RelayRoute | string | null

export interface BaseRecord {
  id: ID
  impressionId: ID
  organizationId: ID
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
  deletedAt: string | null
  deletedBy: string | null
  active: boolean
}

export type BareRecord<R extends BaseRecord> = Omit<R, keyof BaseRecord>

export interface BaseRelayResponse<T extends BaseRecord> {
  [Collection.ChannelLogs]?: T[]
  [Collection.Channels]?: T[]
  [Collection.Departments]?: T[]
  [Collection.Entities]?: T[]
  [Collection.Journeys]?: T[]
  [Collection.FileUploads]?: T[]
  [Collection.ModelTemplates]?: T[]
  [Collection.HelpTopics]?: T[]
  [Collection.Organizations]?: T[]
  [Collection.ProcedureGraphs]?: T[]
  [Collection.TagGroups]?: T[]
  [Collection.Tags]?: T[]
  [Collection.Teams]?: T[]
  [Collection.TicketInteractions]?: T[]
  [Collection.Tickets]?: T[]
  [Collection.TranscriptionKeywords]?: T[]
  [Collection.Users]?: T[]

  // Not actually in a relay response but here for type checking purposes
  [Collection.Session]?: T[]
}

export type Unsafe<T> = T | undefined

export type PartialRecord<T> = { id: ID } & {
  [P in keyof T]?: T[P]
}

export enum ChannelType {
  InboundPhone = 'phone',
  OutboundPhone = 'outbound-phone',
  Email = 'email',
  Chat = 'chat',
  SelfService = 'self-service',
  Voicemail = 'voicemail',
}

export interface OrganizationBranding {
  primaryColor: string
  secondaryColor: string
}

export interface OrganizationFeatures {
  conversationAnalysis?: boolean
  interactionStatistics?: boolean
  workflow?: boolean
  guides?: boolean
}

interface FreePlanOption {
  name: string
  price: number
  free: true
  stripeUserProductId?: string
  stripeSelfServeProductId?: string
}

interface PaidPlanOption {
  name: string
  price: number
  free: false
  stripeUserProductId: string
  stripeSelfServeProductId: string
}

export type PlanOption = PaidPlanOption | FreePlanOption

export interface PaymentInfo {
  stripeUserProductId?: string
  stripeSelfServeProductId?: string
  free: boolean
}

export interface OrganizationRecord extends BaseRecord {
  name: string
  nameSlug: string
  emailDomain: string
  locale: { countryCode: string; postalCode: string }
  branding: null | OrganizationBranding
  generatedRecords: {
    highlights: Record<string, ID>
  }
  features?: OrganizationFeatures
  thirdPartyResources: {
    twilio: {
      maxPhoneNumbers: number
      phoneNumbers: string[]
    }
  }
  settings: {
    enableConversationAnalysis: Nullable<boolean>
    shortAbandonSeconds: number
  }
  happituVersion: string
  availablePlanOptions: PlanOption[]
  // NOTE: Payment info is not returned by the API, but is used when creating an organization
  paymentInfo?: PaymentInfo
  parentOrganizationId: Nullable<ID>
  accountType: 'multi-tenant' | 'standalone' | 'tenant'
  setup: { welcome: boolean }
}

export interface UserOptions {
  channels: string
  tagGroups: string
  tags: string
  teams: string
  workflows: string
  team: string
  users: string
  userGroups: string
  userGroupMembers: string
  userGroupSubGroups: string
  session: string
  dashboard: string
  helpCenter: string
  breakdown: string
  transcriptionTuning: string
  contactLogs: string
  guides: string
  visualDesigner: string
  workflowViewer: string
  templates: string
  wizard: string
  lookup: string
  homepage?: string
  setup?: string
}

export enum UserType {
  Agent = 'Agent',
  Admin = 'Admin',
  Proxy = 'Proxy',
}

export interface UserRecord extends BaseRecord {
  avatarFileId: ID | null
  userType: UserType
  email: string
  firstName: string
  lastName: string
  friendlyName: string
  options: UserOptions
  isSystemUser: boolean
  wizard: boolean
  lastSeen: string
  role: UserRole
  jobTitle: string
}

export type TwilioNumber = {
  phoneNumber: string
  token: string
}

export type Error = {
  type: string
  what: string
}
